import type { FederalState } from '@kimuno/api-sdk';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { updateMe as updateMeAction } from '../../../actions/user.actions';
import { StateSelected } from '../../../ampli';
import OnboardingStepBundeslandImage from '../../../assets/onboarding/onboarding-step-bundesland.svg';
import { selectUser } from '../../../reducers/user.selectors';
import type { OnboardingSteps } from '../../../routes/onboarding/OnboardingPage';
import services from '../../../services';
import useAction from '../../../utils/useAction';
import OnboardingOptionChipList, { type Option } from '../OnboardingOptionChipList';
import OnboardingStepContainer from '../OnboardingStepContainer';
import OnboardingStepImage from '../OnboardingStepImage';

interface OwnProps {
	setOnboardingStep: (step: keyof OnboardingSteps) => void;
	stepNum?: number;
	totalSteps?: number;
	onBackOverride?: (() => void) | null;
	onNextOverride?: () => void;
	hideImage?: boolean;
	compact?: boolean;
}

const OnboardingStepBundesland: React.FC<OwnProps> = ({
	setOnboardingStep,
	stepNum,
	totalSteps,
	onBackOverride,
	onNextOverride,
	hideImage,
	compact,
}) => {
	const { t } = useTranslation();
	const [federalStates, setFederalStates] = useState<Option[]>([]);
	const updateMe = useAction(updateMeAction);
	const user = useSelector(selectUser);
	const selectedSchoolState = user?.school?.state ?? user?.federalState;

	const contentHeights = {
		default: 'calc(100vh - 503px)',
		smallScreen: 'calc(100vh - 309px)',
	};

	const selectOption = (value: string) => {
		setFederalStates((prevStates) =>
			prevStates.map((state) => ({
				...state,
				isSelected: state.value === value,
			}))
		);
	};

	const defaultOnBack = () => setOnboardingStep('schule');

	const onClickNext = async () => {
		const selectedState = federalStates.find((state) => state.isSelected);
		await updateMe({ federalState: selectedState?.value });
		if (user) {
			services.track.ampliEventTrack({
				event: new StateSelected({
					state: selectedState?.value,
				}),
				userProperties: {
					id: user.id,
					federalState: selectedState?.value,
				},
			});
		}
		if (onNextOverride) {
			onNextOverride();
		} else {
			setOnboardingStep('schulstufe');
		}
	};

	useEffect(() => {
		services.text.fetchFederalStates().then((states: FederalState[]) => {
			setFederalStates(
				states.map((state) => ({
					...state,
					value: state.name,
					isSelected: state.name === selectedSchoolState,
				}))
			);
		});
	}, [selectedSchoolState]);

	return (
		<>
			<OnboardingStepContainer
				title={t('In which state do you teach?')}
				subtitle={t('Select one or more federal states.')}
				stepNum={stepNum ?? 3}
				totalSteps={totalSteps}
				onNext={onClickNext}
				onBack={onBackOverride === undefined ? defaultOnBack : (onBackOverride ?? undefined)}
				contentHeights={contentHeights}
				compact={compact}
			>
				<OnboardingOptionChipList
					options={federalStates}
					onItemClick={selectOption}
					isShowOrText={true}
				/>
			</OnboardingStepContainer>
			{!hideImage && <OnboardingStepImage stepImage={<OnboardingStepBundeslandImage />} />}
		</>
	);
};

export default OnboardingStepBundesland;
